/**
 * Depot Capacity Check: the planning model behind the public /capacity-check
 * page. Pure functions, no I/O, no tenant data. Everything runs in the
 * visitor's browser.
 *
 * The day is 48 half-hour slots (slot 0 is 00:00 to 00:30). A van's charging
 * window is every whole half hour inside its dwell at the depot. Two ways of
 * charging are compared:
 *
 *   * charge on arrival: every van takes full charger power from its first
 *     slot until it is full, so the whole fleet's demand stacks up at once;
 *   * charge on arrival held to the connection: the same, but the vans share
 *     the connection's spare capacity, as simple load management does. This
 *     is the plan the saving is measured against when charging on arrival
 *     would take the site over its connection, because the site could not
 *     run that plan;
 *   * smart charging: the fleet's energy is water filled into the lowest
 *     slots of the window, never taking the site over its connection. A van
 *     it cannot fill is counted as not fitting.
 *
 * The connection in kW is the agreed capacity in kVA times the power factor.
 * Each van's energy is miles per day divided by efficiency, plus 10% for
 * charging losses. Vans are identical and arrive and leave together.
 *
 * A planning estimate from the figures entered, not a DNO connection
 * assessment.
 */

export const SLOTS = 48;
/** Hours per slot. */
export const SLOT_HOURS = 0.5;
/** Energy drawn from the grid per kWh stored: 10% charging losses. */
export const LOSS_FACTOR = 1.1;
/** DNO peak, 16:00 to 19:00, as [first slot, slot after last). */
export const DNO_PEAK: readonly [number, number] = [32, 38];
/** Day load applies from 07:00 to 19:00. */
const DAY_LOAD_SLOTS: readonly [number, number] = [14, 38];
/** Night rate applies from 00:00 to 07:00. */
const NIGHT_RATE_END_SLOT = 14;

/** The raw form values, as typed. */
export interface CapacityInputs {
  /** Agreed capacity, kVA. */
  kva: string;
  /** Site load by day, 07:00 to 19:00, kW. */
  dayLoadKw: string;
  /** Site load at night, kW. */
  nightLoadKw: string;
  /** Vans to run electric. */
  vans: string;
  /** Back at depot, "HH:MM". */
  arrive: string;
  /** Out next morning, "HH:MM". */
  depart: string;
  /** Miles per van per day. */
  milesPerDay: string;
  /** Efficiency, miles per kWh. */
  milesPerKwh: string;
  /** Charger per van, kW. */
  chargerKw: string;
  /** Day rate, pence per kWh. */
  dayRatePence: string;
  /** Night rate, 00:00 to 07:00, pence per kWh. */
  nightRatePence: string;
  /** Operating days per year. */
  operatingDays: string;
  /** Power factor, clamped to 0.5 to 1. */
  powerFactor: string;
}

export const DEFAULT_INPUTS: CapacityInputs = {
  kva: "200",
  dayLoadKw: "60",
  nightLoadKw: "20",
  vans: "40",
  arrive: "18:00",
  depart: "06:00",
  milesPerDay: "70",
  milesPerKwh: "2.5",
  chargerKw: "7",
  dayRatePence: "24",
  nightRatePence: "12",
  operatingDays: "260",
  powerFactor: "0.95",
};

export const CHARGER_OPTIONS: readonly { kw: number; label: string }[] = [
  { kw: 7, label: "7 kW AC" },
  { kw: 11, label: "11 kW AC" },
  { kw: 22, label: "22 kW AC" },
];

/** The model's working parameters, derived from the inputs. */
export interface Params {
  /** Connection, kW. */
  cap: number;
  /** Site load without vans, kW per slot. */
  base: number[];
  /** Price, pence per kWh per slot. */
  price: number[];
  /** Grid energy per van per night, kWh, losses included. */
  energy: number;
  /** Charger power per van, kW. */
  kw: number;
  /** Vans the visitor wants to run. */
  n: number;
  /** Slots each van is plugged in, in time order from arrival. */
  win: number[];
  /** Operating days per year. */
  days: number;
}

/** A form value as a number, or the fallback if it is blank or not a number. */
export function toNumber(value: string | undefined, fallback: number): number {
  const v = parseFloat(value ?? "");
  return Number.isFinite(v) ? v : fallback;
}

/** Minutes after midnight for "HH:MM". */
export function minutesOf(t: string): number {
  const [h, m] = (t || "00:00").split(":").map(Number);
  return (h * 60 + m) % 1440;
}

/** "HH:MM" at the start of slot s (wraps past midnight). */
export function slotLabel(s: number): string {
  return `${String(Math.floor((s % SLOTS) / 2)).padStart(2, "0")}:${s % 2 ? "30" : "00"}`;
}

/** Whole half-hour slots inside the dwell from arrival to departure. */
export function windowSlots(arrive: string, depart: string): number[] {
  const a = minutesOf(arrive);
  const dwell = (((minutesOf(depart) - a) % 1440) + 1440) % 1440 || 1440;
  const first = Math.ceil(a / 30);
  const last = Math.floor((a + dwell) / 30);
  const out: number[] = [];
  for (let s = first; s < last; s++) out.push(s % SLOTS);
  return out;
}

export function buildParams(inputs: CapacityInputs): Params {
  const pf = Math.min(1, Math.max(0.5, toNumber(inputs.powerFactor, 0.95)));
  const cap = toNumber(inputs.kva, 200) * pf;
  const day = toNumber(inputs.dayLoadKw, 60);
  const night = toNumber(inputs.nightLoadKw, 20);
  const base = Array.from({ length: SLOTS }, (_, s) =>
    s >= DAY_LOAD_SLOTS[0] && s < DAY_LOAD_SLOTS[1] ? day : night,
  );
  const dayp = toNumber(inputs.dayRatePence, 24);
  const nightp = toNumber(inputs.nightRatePence, 12);
  const price = Array.from({ length: SLOTS }, (_, s) => (s < NIGHT_RATE_END_SLOT ? nightp : dayp));
  const energy = (toNumber(inputs.milesPerDay, 70) / Math.max(0.1, toNumber(inputs.milesPerKwh, 2.5))) * LOSS_FACTOR;
  return {
    cap,
    base,
    price,
    energy,
    kw: toNumber(inputs.chargerKw, 7),
    n: Math.max(1, Math.round(toNumber(inputs.vans, 40))),
    win: windowSlots(inputs.arrive, inputs.depart),
    days: toNumber(inputs.operatingDays, 260),
  };
}

export interface ChargePlan {
  /** Charging load, kW per slot, for the whole fleet. */
  load: number[];
  /** True if some van could not be filled. */
  short: boolean;
}

/** Charge on arrival: every van at full power from its first slot. */
export function chargeOnArrival(p: Params, n: number): ChargePlan {
  const load = new Array<number>(SLOTS).fill(0);
  let rem = p.energy;
  for (const s of p.win) {
    if (rem <= 1e-9) break;
    const take = Math.min(rem, p.kw * SLOT_HOURS);
    load[s] += (n * take) / SLOT_HOURS;
    rem -= take;
  }
  return { load, short: rem > 1e-6 };
}

/**
 * Charge on arrival held to the connection: every van starts on arrival and
 * the vans share the connection's spare capacity (connection minus site
 * load) equally, each up to its charger's power, until full.
 */
export function chargeOnArrivalWithinConnection(p: Params, n: number): ChargePlan {
  const load = new Array<number>(SLOTS).fill(0);
  let rem = p.energy;
  for (const s of p.win) {
    if (rem <= 1e-9) break;
    const perVan = Math.min(p.kw, Math.max(0, p.cap - p.base[s]) / n, rem / SLOT_HOURS);
    load[s] += n * perVan;
    rem -= perVan * SLOT_HOURS;
  }
  return { load, short: rem > 1e-6 };
}

type CapAt = (s: number) => number;

function room(p: Params, n: number, s: number, capAt: CapAt): number {
  return Math.max(0, Math.min(n * p.kw, capAt(s) - p.base[s]));
}

/**
 * Water fill `energy` kWh into `slots` under the connection, lowest site
 * import first. Identical vans share each slot evenly. Adds to `load` in
 * place and returns the energy it could not place.
 */
function fill(p: Params, n: number, slots: number[], energy: number, capAt: CapAt, load: number[]): number {
  const lim = (s: number) => room(p, n, s, capAt) - load[s];
  const total = slots.reduce((a, s) => a + Math.max(0, lim(s)) * SLOT_HOURS, 0);
  if (total + 1e-9 < energy) {
    slots.forEach((s) => {
      load[s] += Math.max(0, lim(s));
    });
    return energy - total;
  }
  let lo = 0;
  let hi = 1e6;
  for (let i = 0; i < 60; i++) {
    const level = (lo + hi) / 2;
    const got = slots.reduce(
      (a, s) => a + Math.min(Math.max(0, lim(s)), Math.max(0, level - p.base[s] - load[s])) * SLOT_HOURS,
      0,
    );
    if (got >= energy) hi = level;
    else lo = level;
  }
  const adds = slots.map((s) => Math.min(Math.max(0, lim(s)), Math.max(0, hi - p.base[s] - load[s])));
  slots.forEach((s, i) => {
    load[s] += adds[i];
  });
  return 0;
}

export interface SmartOptions {
  /** Fill the cheapest price tier first, then the next, for cost. */
  byPrice?: boolean;
  /** Import limit per slot, kW. Defaults to the connection everywhere. */
  capAt?: CapAt;
}

/** Smart charging: water fill under the connection, optionally cheapest tier first. */
export function smartCharge(p: Params, n: number, opts: SmartOptions = {}): ChargePlan {
  const byPrice = opts.byPrice ?? false;
  const capAt: CapAt = opts.capAt ?? (() => p.cap);
  const load = new Array<number>(SLOTS).fill(0);
  let need = n * p.energy;
  if (!byPrice) {
    need = fill(p, n, p.win, need, capAt, load);
  } else {
    const tiers = Array.from(new Set(p.win.map((s) => p.price[s]))).sort((a, b) => a - b);
    for (const t of tiers) {
      if (need <= 1e-9) break;
      need = fill(p, n, p.win.filter((s) => p.price[s] === t), need, capAt, load);
    }
  }
  return { load, short: need > 1e-6 };
}

/** Site import per slot: site load plus charging. */
export function siteImport(p: Params, load: number[]): number[] {
  return p.base.map((b, s) => b + load[s]);
}

export function peakOf(arr: number[]): number {
  return Math.max(...arr);
}

export function fitsOnArrival(p: Params, n: number): boolean {
  const u = chargeOnArrival(p, n);
  return !u.short && peakOf(siteImport(p, u.load)) <= p.cap + 1e-6;
}

export function fitsSmart(p: Params, n: number): boolean {
  return !smartCharge(p, n).short;
}

/** Largest n in 1..limit for which `ok(n)` holds, assuming it is monotonic. 0 if none. */
export function largest(ok: (n: number) => boolean, limit = 5000): number {
  if (!ok(1)) return 0;
  let lo = 1;
  let hi = 2;
  while (hi <= limit && ok(hi)) {
    lo = hi;
    hi *= 2;
  }
  hi = Math.min(hi, limit + 1);
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (ok(m)) lo = m;
    else hi = m;
  }
  return lo;
}

/** Charging cost of a plan for one day, pounds. */
export function dailyCost(p: Params, load: number[]): number {
  return load.reduce((a, kw, s) => a + kw * SLOT_HOURS * p.price[s], 0) / 100;
}

export interface TimedProfile {
  /** Import needed 16:00 to 19:00, kW, rounded up to 5 kW. */
  peak: number;
  /** Highest import outside 16:00 to 19:00, kW, rounded up to 10 kW. */
  off: number;
}

/**
 * The lowest import the site needs during the DNO peak, 16:00 to 19:00, with
 * smart charging for n vans, in 5 kW steps. Null if the vans do not fit even
 * on the full connection.
 */
export function timedProfile(p: Params, n: number): TimedProfile | null {
  const peak = new Set<number>();
  for (let s = DNO_PEAK[0]; s < DNO_PEAK[1]; s++) peak.add(s);
  const floor = Math.max(...Array.from(peak).map((s) => p.base[s]));
  const ok = (limit: number) => !smartCharge(p, n, { capAt: (s) => (peak.has(s) ? limit : p.cap) }).short;
  if (!ok(p.cap)) return null;
  let lo = floor;
  let hi = p.cap;
  if (ok(floor)) hi = floor;
  else
    for (let i = 0; i < 40; i++) {
      const m = (lo + hi) / 2;
      if (ok(m)) hi = m;
      else lo = m;
    }
  const stepped = Math.ceil(hi / 5) * 5;
  const plan = smartCharge(p, n, { capAt: (s) => (peak.has(s) ? Math.min(stepped, p.cap) : p.cap) });
  const st = siteImport(p, plan.load);
  const off = Math.max(...st.filter((_, s) => !peak.has(s)));
  return { peak: Math.min(stepped, p.cap), off: Math.ceil(off / 10) * 10 };
}

export interface CapacityResult {
  params: Params;
  /** Most vans that fit charged on arrival. */
  maxOnArrival: number;
  /** Most vans that fit with smart charging. */
  maxSmart: number;
  /** Plans for the n vans the visitor entered. */
  onArrival: ChargePlan;
  smart: ChargePlan;
  smartForCost: ChargePlan;
  onArrivalSite: number[];
  smartSite: number[];
  onArrivalPeak: number;
  smartPeak: number;
  /** A single van cannot take its energy in the dwell at its charger power. */
  tooShort: boolean;
  /**
   * Charging cost difference per year, baseline minus smart for cost, pounds.
   * The baseline is charging on arrival if it fits the connection, otherwise
   * charging on arrival held to the connection. Null, with
   * `savingNotStatedReason`, when either plan leaves a van short.
   */
  annualCostDifference: number | null;
  /** The plan the saving is measured against. */
  savingBaseline: "on_arrival" | "on_arrival_within_connection";
  /** Why no saving is shown, when `annualCostDifference` is null. */
  savingNotStatedReason: string | null;
  /** Import needed in the DNO peak. Null if smart charging does not fit. */
  timed: TimedProfile | null;
}

export function evaluate(inputs: CapacityInputs): CapacityResult {
  const p = buildParams(inputs);
  const maxOnArrival = largest((n) => fitsOnArrival(p, n));
  const maxSmart = largest((n) => fitsSmart(p, n));
  const n = p.n;
  const onArrival = chargeOnArrival(p, n);
  const smart = smartCharge(p, n);
  const smartForCost = smartCharge(p, n, { byPrice: true });
  const onArrivalSite = siteImport(p, onArrival.load);
  const smartSite = siteImport(p, smart.load);
  const tooShort = p.energy > p.kw * p.win.length * SLOT_HOURS + 1e-6;
  const smartFits = !smart.short && !smartForCost.short;
  const arrivalFits = peakOf(onArrivalSite) <= p.cap + 1e-6;
  const baseline = arrivalFits ? onArrival : chargeOnArrivalWithinConnection(p, n);
  const savingBaseline = arrivalFits ? "on_arrival" : "on_arrival_within_connection";
  const savingNotStatedReason = baseline.short
    ? arrivalFits
      ? "Charged on arrival, a van could not take its energy before it leaves, so there is no workable plan to compare smart charging with."
      : "Charged on arrival within the connection, the vans could not take their energy before they leave, so there is no workable plan to compare smart charging with."
    : !smartFits
      ? "Smart charging cannot fit every van on this connection, so its cost covers less energy than the fleet needs."
      : null;
  return {
    params: p,
    maxOnArrival,
    maxSmart,
    onArrival,
    smart,
    smartForCost,
    onArrivalSite,
    smartSite,
    onArrivalPeak: peakOf(onArrivalSite),
    smartPeak: peakOf(smartSite),
    tooShort,
    annualCostDifference:
      savingNotStatedReason === null ? (dailyCost(p, baseline.load) - dailyCost(p, smartForCost.load)) * p.days : null,
    savingBaseline,
    savingNotStatedReason,
    timed: smartFits ? timedProfile(p, n) : null,
  };
}
